export type VideoEffectMode = "none" | "avatar" | "blur" | "both";

type SmoothedFace = {
  x: number;
  y: number;
  width: number;
  height: number;
  seenAt: number;
};

type WorkerOutput =
  | { type: "ready"; faceReady: boolean; maskReady: boolean }
  | { type: "error"; message: string }
  | {
      type: "result";
      timestamp: number;
      face?: { x: number; y: number; width: number; height: number };
      mask?: ArrayBuffer;
      maskWidth?: number;
      maskHeight?: number;
    };

export const coverDimensions = (
  sourceWidth: number,
  sourceHeight: number,
  containerWidth: number,
  containerHeight: number,
): { width: number; height: number } => {
  if (
    sourceWidth <= 0 ||
    sourceHeight <= 0 ||
    containerWidth <= 0 ||
    containerHeight <= 0
  ) {
    return { width: 0, height: 0 };
  }
  const scale = Math.max(
    containerWidth / sourceWidth,
    containerHeight / sourceHeight,
  );
  return {
    width: sourceWidth * scale,
    height: sourceHeight * scale,
  };
};

export const avatarFontSize = (
  faceWidth: number,
  faceHeight: number,
  mobile: boolean,
): number => Math.max(faceWidth, faceHeight) * (mobile ? 1.28 : 1.72);

const isMobileVisualBrowser = (): boolean =>
  typeof navigator !== "undefined" &&
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

export class VisionPipeline {
  private readonly worker = new Worker(
    new URL("./vision.worker.ts", import.meta.url),
  );
  private face: SmoothedFace | undefined;
  private mask: ImageData | undefined;
  private animationFrame = 0;
  private lastVisionRun = 0;
  private started = false;
  private faceInitialized = false;
  private maskInitialized = false;
  private workerBusy = false;
  private initializationRequested = false;
  private initializationFailed = false;
  private cameraEnabled = true;
  private mode: VideoEffectMode = "none";
  private avatar = "😀";
  private readonly mobileVisuals = isMobileVisualBrowser();
  private displayContainer: HTMLElement | undefined;

  private readonly outputContext: CanvasRenderingContext2D;
  private readonly personCanvas = document.createElement("canvas");
  private readonly personContext: CanvasRenderingContext2D;
  private readonly maskCanvas = document.createElement("canvas");
  private readonly maskContext: CanvasRenderingContext2D;
  private readonly displayResizeObserver: ResizeObserver;

  constructor(
    private readonly video: HTMLVideoElement,
    readonly canvas: HTMLCanvasElement,
    private readonly onState: (state: string) => void,
  ) {
    const outputContext = canvas.getContext("2d", { alpha: false });
    const personContext = this.personCanvas.getContext("2d");
    const maskContext = this.maskCanvas.getContext("2d");
    if (!outputContext || !personContext || !maskContext) {
      throw new Error("Canvas 2D를 사용할 수 없습니다.");
    }
    this.outputContext = outputContext;
    this.personContext = personContext;
    this.maskContext = maskContext;
    this.displayResizeObserver = new ResizeObserver(() => {
      this.fitCanvasToContainer();
    });
    this.worker.onmessage = (event: MessageEvent<WorkerOutput>) => {
      this.handleWorkerMessage(event.data);
    };
    this.worker.onerror = () => {
      this.workerBusy = false;
      this.initializationRequested = false;
      this.initializationFailed = true;
      this.onState("영상 효과를 사용할 수 없음");
    };
  }

  private initializeForCurrentMode(): void {
    const needFace =
      (this.mode === "avatar" || this.mode === "both") &&
      !this.faceInitialized;
    const needMask =
      (this.mode === "blur" || this.mode === "both") &&
      !this.maskInitialized;
    if (
      (!needFace && !needMask) ||
      this.initializationRequested ||
      this.initializationFailed
    ) {
      return;
    }
    this.initializationRequested = true;
    this.onState("영상 효과 모델 불러오는 중");
    this.worker.postMessage({ type: "initialize", needFace, needMask });
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.render = this.render.bind(this);
    this.observeDisplayContainer();
    this.animationFrame = requestAnimationFrame(this.render);
    this.onState(this.describeState());
  }

  stop(): void {
    this.started = false;
    cancelAnimationFrame(this.animationFrame);
    this.displayResizeObserver.disconnect();
    this.displayContainer = undefined;
    this.worker.terminate();
  }

  setMode(mode: VideoEffectMode): void {
    this.mode = mode;
    this.face = undefined;
    this.mask = undefined;
    this.initializationFailed = false;
    this.onState(this.describeState());
    this.initializeForCurrentMode();
  }

  getMode(): VideoEffectMode {
    return this.mode;
  }

  setAvatar(avatar: string): void {
    this.avatar = avatar.slice(0, 2);
  }

  setCameraEnabled(enabled: boolean): void {
    this.cameraEnabled = enabled;
  }

  private handleWorkerMessage(message: WorkerOutput): void {
    if (message.type === "ready") {
      this.faceInitialized = message.faceReady;
      this.maskInitialized = message.maskReady;
      this.initializationRequested = false;
      this.onState(this.describeState());
      this.initializeForCurrentMode();
      return;
    }
    if (message.type === "error") {
      this.workerBusy = false;
      this.initializationRequested = false;
      this.initializationFailed = true;
      this.onState("영상 효과를 사용할 수 없음");
      console.error("MediaPipe worker initialization failed", message.message);
      return;
    }

    this.workerBusy = false;
    if (message.face) {
      this.updateFace(
        {
          x: message.face.x * this.canvas.width,
          y: message.face.y * this.canvas.height,
          width: message.face.width * this.canvas.width,
          height: message.face.height * this.canvas.height,
        },
        performance.now(),
      );
    }
    if (
      message.mask &&
      message.maskWidth &&
      message.maskHeight
    ) {
      const values = new Uint8Array(message.mask);
      const rgba = new Uint8ClampedArray(values.length * 4);
      for (let index = 0; index < values.length; index += 1) {
        const offset = index * 4;
        rgba[offset] = 255;
        rgba[offset + 1] = 255;
        rgba[offset + 2] = 255;
        rgba[offset + 3] = values[index] === 0 ? 0 : 255;
      }
      this.mask = new ImageData(rgba, message.maskWidth, message.maskHeight);
    }
  }

  private render(now: number): void {
    if (!this.started) return;
    this.observeDisplayContainer();
    this.resizeToVideo();

    if (
      this.cameraEnabled &&
      this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
    ) {
      this.requestVisionFrame(now);
      this.drawFrame(now);
    } else {
      this.drawSafePlaceholder("CAMERA OFF");
    }
    this.animationFrame = requestAnimationFrame(this.render);
  }

  private resizeToVideo(): void {
    const width = this.video.videoWidth || 1280;
    const height = this.video.videoHeight || 720;
    if (this.canvas.width === width && this.canvas.height === height) return;
    this.canvas.width = width;
    this.canvas.height = height;
    this.personCanvas.width = width;
    this.personCanvas.height = height;
    this.fitCanvasToContainer();
  }

  private observeDisplayContainer(): void {
    const container = this.canvas.parentElement;
    if (!container || container === this.displayContainer) return;
    this.displayResizeObserver.disconnect();
    this.displayContainer = container;
    this.displayResizeObserver.observe(container);
    this.fitCanvasToContainer();
  }

  private fitCanvasToContainer(): void {
    const container = this.displayContainer;
    if (!container || this.canvas.parentElement !== container) return;
    const dimensions = coverDimensions(
      this.canvas.width,
      this.canvas.height,
      container.clientWidth,
      container.clientHeight,
    );
    if (dimensions.width === 0 || dimensions.height === 0) return;
    this.canvas.style.width = `${dimensions.width}px`;
    this.canvas.style.height = `${dimensions.height}px`;
  }

  private requestVisionFrame(now: number): void {
    const needsFace = this.mode === "avatar" || this.mode === "both";
    const needsMask = this.mode === "blur" || this.mode === "both";
    if (
      (needsFace && !this.faceInitialized) ||
      (needsMask && !this.maskInitialized) ||
      this.workerBusy ||
      (!needsFace && !needsMask) ||
      now - this.lastVisionRun < 80
    ) {
      return;
    }
    this.lastVisionRun = now;
    this.workerBusy = true;
    void createImageBitmap(this.video, {
      resizeWidth: 384,
      resizeHeight: 216,
      resizeQuality: "low",
    })
      .then((bitmap) => {
        this.worker.postMessage(
          {
            type: "frame",
            bitmap,
            timestamp: Math.round(now),
            needFace: needsFace,
            needMask: needsMask,
          },
          [bitmap],
        );
      })
      .catch((error: unknown) => {
        this.workerBusy = false;
        console.error("Video frame transfer failed", error);
      });
  }

  private updateFace(
    box: { x: number; y: number; width: number; height: number },
    now: number,
  ): void {
    if (!this.face) {
      this.face = { ...box, seenAt: now };
      return;
    }
    const smoothing = 0.28;
    this.face.x += (box.x - this.face.x) * smoothing;
    this.face.y += (box.y - this.face.y) * smoothing;
    this.face.width += (box.width - this.face.width) * smoothing;
    this.face.height += (box.height - this.face.height) * smoothing;
    this.face.seenAt = now;
  }

  private drawFrame(now: number): void {
    const needsFace = this.mode === "avatar" || this.mode === "both";
    const needsMask = this.mode === "blur" || this.mode === "both";
    const hasRecentFace = Boolean(this.face && now - this.face.seenAt <= 500);

    if ((needsFace && !hasRecentFace) || (needsMask && !this.mask)) {
      this.drawSafePlaceholder(
        this.effectIsReady() ? "TRACKING…" : "EFFECT SAFE MODE",
      );
      return;
    }

    if (needsMask && this.mask) {
      this.outputContext.save();
      this.outputContext.filter = "blur(16px)";
      this.outputContext.drawImage(
        this.video,
        -22,
        -22,
        this.canvas.width + 44,
        this.canvas.height + 44,
      );
      this.outputContext.restore();

      this.maskCanvas.width = this.mask.width;
      this.maskCanvas.height = this.mask.height;
      this.maskContext.putImageData(this.mask, 0, 0);
      this.personContext.clearRect(
        0,
        0,
        this.personCanvas.width,
        this.personCanvas.height,
      );
      this.personContext.globalCompositeOperation = "source-over";
      this.personContext.drawImage(
        this.video,
        0,
        0,
        this.personCanvas.width,
        this.personCanvas.height,
      );
      this.personContext.globalCompositeOperation = "destination-in";
      this.personContext.filter = "blur(2px)";
      this.personContext.drawImage(
        this.maskCanvas,
        0,
        0,
        this.personCanvas.width,
        this.personCanvas.height,
      );
      this.personContext.filter = "none";
      this.personContext.globalCompositeOperation = "source-over";
      this.outputContext.drawImage(this.personCanvas, 0, 0);
    } else {
      this.outputContext.drawImage(
        this.video,
        0,
        0,
        this.canvas.width,
        this.canvas.height,
      );
    }

    if (needsFace && this.face) {
      this.drawAvatar(this.face);
    }
  }

  private drawAvatar(face: SmoothedFace): void {
    const context = this.outputContext;
    const size = avatarFontSize(
      face.width,
      face.height,
      this.mobileVisuals,
    );
    const centerX = face.x + face.width / 2;
    const centerY = face.y + face.height * 0.47;

    context.save();
    context.font = `${Math.round(size)}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(this.avatar, centerX, centerY + size * 0.01);
    context.restore();
  }

  private drawSafePlaceholder(label: string): void {
    const context = this.outputContext;
    const width = this.canvas.width;
    const height = this.canvas.height;
    const gradient = context.createRadialGradient(
      width / 2,
      height / 2,
      0,
      width / 2,
      height / 2,
      Math.max(width, height) * 0.62,
    );
    gradient.addColorStop(0, "#17323c");
    gradient.addColorStop(1, "#071017");
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
    context.fillStyle = "#36d4c7";
    context.font = `${Math.max(54, Math.round(height * 0.19))}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(this.avatar, width / 2, height / 2 - height * 0.03);
    context.fillStyle = "#789099";
    context.font = `500 ${Math.max(12, Math.round(height * 0.025))}px Bahnschrift, sans-serif`;
    context.letterSpacing = "0.18em";
    context.fillText(label, width / 2, height / 2 + height * 0.18);
  }

  private describeState(): string {
    if (this.mode === "none") return "로컬 원본 처리";
    if (this.initializationFailed) return "영상 효과를 사용할 수 없음";
    if (!this.effectIsReady()) return "영상 효과 모델 불러오는 중";
    if (this.mode === "avatar") return "얼굴 추적 사용 중";
    if (this.mode === "blur") return "배경 분리 사용 중";
    return "얼굴 추적 + 배경 분리";
  }

  private effectIsReady(): boolean {
    const needsFace = this.mode === "avatar" || this.mode === "both";
    const needsMask = this.mode === "blur" || this.mode === "both";
    return (
      (!needsFace || this.faceInitialized) &&
      (!needsMask || this.maskInitialized)
    );
  }
}
